import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  CircularProgress, 
  Box, 
  Typography, 
  Button,
  IconButton,
  Stack,
  Snackbar,
  Alert,
  Dialog,
  DialogContent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppContext } from '../context/AppContext';
import { createContextAwareApiClient } from '../api/contextAwareApiClient';
import InlineEditForm from './InlineEditForm';
import ConfirmDeleteDialog from './dialogs/ConfirmDeleteDialog';

/**
 * CustomerSelector component displays a dropdown for selecting a customer
 * and manages the global customer context. Also provides controls for
 * creating, editing, and deleting customers using inline editing.
 * 
 * @returns {JSX.Element} - CustomerSelector component
 */
function CustomerSelector() {
  const { 
    customerContext, 
    setCustomer, 
    clearCustomer,
    getContextData,
    validateContext,
    clearFleet
  } = useAppContext();
  
  // Create context-aware API client
  const { customerService, fleetService, canopyService } = useMemo(
    () => createContextAwareApiClient(getContextData, validateContext),
    [getContextData, validateContext]
  );
  
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Inline editing states
  const [isEditing, setIsEditing] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Notification states
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info' // 'error', 'warning', 'info', 'success'
  });

  // Field configuration for customer editing
  const customerFields = [
    {
      name: 'customerId',
      label: 'Customer ID',
      required: true,
      disabled: !!editingCustomer?.customerId, // Read-only if editing existing customer
      helperText: 'Enter a unique customer identifier'
    },
    {
      name: 'customerName',
      label: 'Customer Name',
      required: true,
      helperText: 'Enter the customer name'
    },
    {
      name: 'customerContact',
      label: 'Contact Information',
      required: false,
      multiline: true,
      rows: 3,
      helperText: 'Enter contact information'
    },
    {
      name: 'customerAddress',
      label: 'Address',
      required: false,
      multiline: true,
      rows: 3,
      helperText: 'Enter the customer address'
    },
    {
      name: 'customerLatitude',
      label: 'Latitude',
      required: false,
      type: 'number',
      helperText: 'Enter latitude in decimal degrees (e.g., 25.7617)'
    },
    {
      name: 'customerLongitude',
      label: 'Longitude',
      required: false,
      type: 'number',
      helperText: 'Enter longitude in decimal degrees (e.g., -80.1918)'
    },
    {
      name: 'customerData',
      label: 'Notes',
      required: false,
      multiline: true,
      rows: 3,
      type: 'json',
      helperText: 'Enter additional notes or data'
    }
  ];

  // Fetch customers from API
  const fetchCustomers = useCallback(async () => {
    console.log('=== FETCHING CUSTOMERS ===');
    setLoading(true);
    setError(null);
    
    try {
      const response = await customerService.getCustomers();
      console.log('Customer fetch response:', response);
      
      if (response.success) {
        const customerData = response.data?.data || [];
        console.log('Customer data received:', customerData);
        console.log('Number of customers:', customerData.length);
        
        setCustomers(customerData);
        
        // Log the current customers state after setting
        console.log('Customers state updated with:', customerData);
      } else {
        console.error('Failed to load customers:', response);
        setError('Failed to load customers');
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
      console.log('=== END FETCHING CUSTOMERS ===');
    }
  }, [customerService]);

  // Fetch customers from API on component mount
  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleChange = async (event) => {
    const customerId = event.target.value;
    console.log('Customer selection changed to:', customerId);
    console.log('Available customers:', customers);
    
    if (customerId) {
      const selectedCustomer = customers.find(c => c.customerId === customerId);
      console.log('Selected customer:', selectedCustomer);
      setCustomer(selectedCustomer);
      
      // Clear the fleet context. This will trigger FleetList to refetch
      // fleets for the newly selected customer.
      clearFleet();
      
    } else {
      clearCustomer();
    }
  };
  
  // Handle creating a new customer
  const handleAddCustomer = () => {
    setEditingCustomer({
      customerId: '',
      customerName: '',
      customerContact: '',
      customerAddress: '',
      customerLatitude: '',
      customerLongitude: '',
      customerData: ''
    });
    setIsEditing(true);
  };
  
  // Handle editing a customer
  const handleEditCustomer = () => {
    if (customerContext) {
      setEditingCustomer({ ...customerContext });
      setIsEditing(true);
    }
  };
  
  // Handle deleting a customer
  const handleDeleteClick = () => {
    if (customerContext) {
      setDeleteDialogOpen(true);
    }
  };
  
  // Handle save customer
  const handleSaveCustomer = async (formData) => {
    console.log('=== SAVING CUSTOMER ===');
    console.log('Form data:', formData);
    console.log('Editing customer:', editingCustomer);
    
    setIsSubmitting(true);
    
    try {
      let response;
      
      if (editingCustomer.customerId) {
        // Update existing customer
        console.log('Updating existing customer:', editingCustomer.customerId);
        response = await customerService.updateCustomer(
          editingCustomer.customerId,
          formData
        );
      } else {
        // Create new customer - use customerId from form data
        console.log('Creating new customer with ID:', formData.customerId);
        response = await customerService.createCustomer(formData);
      }
      
      console.log('Save response:', response);
      
      if (response.success) {
        showNotification(
          `Customer ${editingCustomer.customerId ? 'updated' : 'created'} successfully`, 
          'success'
        );
        
        // Refresh customer list
        console.log('Refreshing customer list...');
        await fetchCustomers();
        
        // Select the saved customer - FIX: Wait for the state to update
        const savedCustomerId = editingCustomer.customerId || formData.customerId;
        console.log('Looking for saved customer ID:', savedCustomerId);
        
        // Wait a moment for the state to update, then find and select the customer
        setTimeout(async () => {
          console.log('Current customers after refresh:', customers);
          const savedCustomer = customers.find(c => c.customerId === savedCustomerId);
          console.log('Found saved customer:', savedCustomer);
          
          if (savedCustomer) {
            setCustomer(savedCustomer);
            console.log('Customer selected:', savedCustomer);
          } else {
            console.warn('Could not find saved customer in list');
          }
        }, 100);
        
        // Exit edit mode
        setIsEditing(false);
        setEditingCustomer(null);
      } else {
        console.error('Failed to save customer:', response);
        showNotification(
          `Failed to ${editingCustomer.customerId ? 'update' : 'create'} customer: ${response.message}`, 
          'error'
        );
      }
    } catch (err) {
      console.error('Error saving customer:', err);
      showNotification(
        `An error occurred while ${editingCustomer.customerId ? 'updating' : 'creating'} the customer`, 
        'error'
      );
    } finally {
      setIsSubmitting(false);
      console.log('=== END SAVING CUSTOMER ===');
    }
  };
  
  // Handle cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingCustomer(null);
  };
  
  // Handle finished editing
  const handleFinishedEdit = () => {
    setIsEditing(false);
    setEditingCustomer(null);
  };
  
  // Show notification message
  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };
  
  // Close notification
  const handleNotificationClose = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Handle customer deletion confirmed
  const handleDeleteConfirm = async () => {
    if (!customerContext) return;
    
    setIsDeleting(true);
    
    try {
      // Store customer name for use in success message after deletion
      const customerName = customerContext.customerName;
      const customerId = customerContext.customerId;
      
      console.log(`Preparing to delete customer ${customerId}: "${customerName}"`);
      
      // 1. Check if customer has associated fleets
      console.log(`Checking for fleets associated with customer ${customerId}`);
      const fleetsResponse = await fleetService.getFleetsByCustomerId(customerId);
      const hasFleets = fleetsResponse.success && fleetsResponse.data && 
                       (Array.isArray(fleetsResponse.data) ? fleetsResponse.data.length > 0 : 
                        (fleetsResponse.data.data && fleetsResponse.data.data.length > 0));
      
      const fleets = Array.isArray(fleetsResponse.data) ? fleetsResponse.data : 
                    (fleetsResponse.data && fleetsResponse.data.data ? fleetsResponse.data.data : []);
      
      if (hasFleets) {
        console.log(`Found ${fleets.length} fleets for customer ${customerId}, deleting recursively`);
        
        // 2. Delete each fleet's canopies and then the fleet
        for (const fleet of fleets) {
          console.log(`Processing fleet ${fleet.fleet_id}`);
          const canopiesResponse = await canopyService.getCanopiesByFleetId(fleet.fleet_id);
          
          const canopies = canopiesResponse.success && canopiesResponse.data ? 
                         (Array.isArray(canopiesResponse.data) ? canopiesResponse.data : 
                          (canopiesResponse.data.data ? canopiesResponse.data.data : [])) : 
                         [];
          
          if (canopies.length > 0) {
            console.log(`Found ${canopies.length} canopies for fleet ${fleet.fleet_id}, deleting them first`);
            
            // 2a. Delete canopies first
            for (const canopy of canopies) {
              console.log(`Deleting canopy ${canopy.canopy_id}`);
              const canopyDeleteResponse = await canopyService.deleteCanopy(canopy.canopy_id);
              
              if (!canopyDeleteResponse.success) {
                console.error(`Failed to delete canopy ${canopy.canopy_id}:`, canopyDeleteResponse);
              }
            }
          }
          
          // 2b. Then delete the fleet
          console.log(`Deleting fleet ${fleet.fleet_id}`);
          const fleetDeleteResponse = await fleetService.deleteFleet(fleet.fleet_id);
          
          if (!fleetDeleteResponse.success) {
            console.error(`Failed to delete fleet ${fleet.fleet_id}:`, fleetDeleteResponse);
          }
        }
      }
      
      // 3. Finally delete the customer
      console.log(`Deleting customer ${customerId}`);
      const response = await customerService.deleteCustomer(customerId);
      
      // Always close dialog after attempt
      setDeleteDialogOpen(false);
      
      if (response.success) {
        console.log('Customer deletion successful:', response);
        // Show success notification
        showNotification(`Customer "${customerName}" successfully deleted`, 'success');
        
        // Clear selection
        clearCustomer();
        
        // Refresh customer list
        fetchCustomers();
      } else {
        console.error('Failed to delete customer:', response);
        // Show error notification with specific message if available
        showNotification(
          `Failed to delete customer: ${response.message || 'Unknown error'}`, 
          'error'
        );
      }
    } catch (err) {
      console.error('Error deleting customer:', err);
      // Close dialog and show error notification
      setDeleteDialogOpen(false);
      showNotification(`Error: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  // Display error message if API call fails
  if (error) {
    return (
      <Box>
        <Typography variant="h6">Customers</Typography>
        <Typography color="error" variant="body2">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" component="div">
          Customer Selection
        </Typography>
        <Button 
          startIcon={<AddIcon />} 
          size="small" 
          variant="outlined"
          onClick={handleAddCustomer}
          disabled={loading || isEditing}
        >
          New Customer
        </Button>
      </Stack>
      
      {/* Customer editing is modal so the fixed application shell cannot clip it. */}
      <Dialog
        open={isEditing && !!editingCustomer}
        onClose={isSubmitting ? undefined : handleCancelEdit}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        aria-labelledby="customer-editor-title"
        PaperProps={{
          sx: {
            m: { xs: 1, sm: 2 },
            maxHeight: { xs: 'calc(100dvh - 16px)', sm: 'calc(100dvh - 32px)' },
            overflow: 'hidden',
          },
        }}
      >
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          {editingCustomer && (
            <InlineEditForm
              data={editingCustomer}
              fields={customerFields}
              onSave={handleSaveCustomer}
              onCancel={handleCancelEdit}
              onFinished={handleFinishedEdit}
              isSubmitting={isSubmitting}
              title={editingCustomer.customerId ? "Edit Customer" : "Create New Customer"}
              titleId="customer-editor-title"
              dialogLayout
              saveLabel={editingCustomer.customerId ? 'Update Customer' : 'Create Customer'}
              showFinished={false}
            />
          )}
        </DialogContent>
      </Dialog>
      
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <FormControl fullWidth disabled={loading || isEditing}>
          <InputLabel>Customer</InputLabel>
          <Select 
            value={customerContext?.customerId || ''} 
            onChange={handleChange}
            startAdornment={loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          >
            {customers.map(customer => (
              <MenuItem 
                key={customer.customerId} 
                value={customer.customerId}
              >
                {customer.customerName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <IconButton 
          disabled={!customerContext || loading || isEditing} 
          color="primary" 
          onClick={handleEditCustomer}
        >
          <EditIcon />
        </IconButton>
        
        <IconButton 
          disabled={!customerContext || loading || isEditing} 
          color="error" 
          onClick={handleDeleteClick}
        >
          <DeleteIcon />
        </IconButton>
      </Stack>
      
      {/* Confirm Delete Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Customer"
        message={customerContext ? `Are you sure you want to delete the customer "${customerContext.customerName}"? This will also delete all associated fleets and canopies.` : ''}
        isDeleting={isDeleting}
      />
      
      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleNotificationClose} 
          severity={notification.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CustomerSelector;
